import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { omitBy } from 'es-toolkit';

import { isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { actionOnBroken, actionOnClosed, actionOnError, actionOnMessage } from '@skynet/data-ws-mm';
import { LoanCommands } from '@skynet/loans';
import { staleCoinGeckoLinkPatch } from '@skynet/data-helpers';

import type { JsonLoanTermsSuccess, LoanTermsState } from './types';
import { LoanTermsCommands } from './types';
import type { UpdateLoanPayload } from './actions';
import { addLoan, updateLoan } from './actions';

export const initialState: LoanTermsState = {
  id: undefined,
  details: undefined,
  requestStatus: null,
  resUpdateLoan: null,
  confirmedTerms: undefined,
  error: null,
  isLoanEditing: false, // to handle loan editing mode
};

const allowedCommands = Object.values(LoanTermsCommands);

/**
 * Ends the update in flight as failed and drops its optimistic merge, putting back the
 * details the server kept (see `confirmedTerms`).
 */
const failPendingUpdate = (state: LoanTermsState) => {
  if (state.resUpdateLoan === 'pending' && state.confirmedTerms) {
    state.id = state.confirmedTerms.id;
    state.details = state.confirmedTerms.details;
  }
  state.confirmedTerms = undefined;
  state.resUpdateLoan = 'error';
};

const loanTermsSlice = createSlice({
  name: 'terms',
  initialState,
  reducers: {
    toggleLoanEditing(state, action: { payload: { value?: boolean } }) {
      // `!= null` is intentional here (vs. `!== undefined`): it lets callers pass
      // an explicit `value` to force a state, or omit it (undefined) to flip the
      // current state — `null` and `undefined` are treated the same.
      state.isLoanEditing =
        action.payload.value != null ? action.payload.value : !state.isLoanEditing;
    },
    resetUpdateLoan(state) {
      state.resUpdateLoan = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateLoan.pending, (state) => {
      // Like `resUpdateLoan`, this models one update at a time: a second one sent before the
      // first is acked keeps the first one's baseline, the last state the server confirmed.
      if (state.resUpdateLoan !== 'pending') {
        state.confirmedTerms = { id: state.id, details: state.details };
      }
      state.resUpdateLoan = 'pending';
    });

    builder.addCase(updateLoan.rejected, failPendingUpdate);

    builder.addCase(updateLoan.fulfilled, (state, action) => {
      const updatedData: UpdateLoanPayload = action.payload;
      if (!updatedData || !state.details) {
        return;
      }

      // Merge updated fields over the existing details (defined values win),
      // mirroring lodash `defaults(updatedData, state.details)`.
      state.details = {
        ...state.details,
        ...omitBy(updatedData, (value) => value === undefined),
        tokens: updatedData.tokens ?? state.details.tokens ?? [],
        ...staleCoinGeckoLinkPatch(state.details, updatedData),
      };
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, okCommand } = json;

      if (error) {
        state.error = error;

        // Only errors for commands listed in `LoanTermsCommands` reach here.
        // `parseResponse` sets `skipProcessing = true` for errors whose
        // `errorCommand` is not in `allowedCommands`, so AddLoan errors are
        // silently dropped — the add flow is fire-and-forget; failures arrive
        // as a subsequent `Terms` message or are surfaced by the job system.
        switch (errorCommand) {
          case LoanCommands.UpdateLoan:
            state.requestStatus = 'error';
            failPendingUpdate(state);
            break;
          case undefined:
          default:
        }
      }

      if (command === LoanTermsCommands.Terms) {
        const { details, id }: JsonLoanTermsSuccess = json;
        state.details = {
          ...details,
          tokens: details.tokens ?? [],
        };
        state.id = id;
        state.requestStatus = 'success';
        // The server's own state: nothing older is left to fall back on.
        state.confirmedTerms = undefined;
      }

      if (isOkCommand(command) && okCommand === LoanCommands.UpdateLoan) {
        state.resUpdateLoan = 'success';
        state.confirmedTerms = undefined;
      }
    });

    // The socket carrying the ack went away, so the ack can no longer arrive: settle the
    // update instead of leaving the form's switches locked until the page is reopened.
    // These are every terminal socket event the middleware dispatches (there is no `::CLOSE`).
    // The command may still have reached the server, which re-sends Terms after a reconnect.
    builder.addMatcher(isAnyOf(actionOnClosed, actionOnBroken, actionOnError), (state) => {
      if (state.resUpdateLoan === 'pending') {
        failPendingUpdate(state);
      }
    });
  },
});

export const loanTermsActions = {
  ...loanTermsSlice.actions,
  updateLoan,
  addLoan,
};

export default loanTermsSlice.reducer;
